import { CloseOutlined } from '@ant-design/icons';
import { useFacebookErrorCRUDActions } from '@mm/state/actions/FacebookErrorCRUDActions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  activeChatbotIdSelector,
  selectActiveCompanyId,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FacebookPageErrors } from '../../../../../../common/components/FacebookPageAlerts/FacebookPageErrors';
import { ActivationBlock } from '../ActivationBlock/ActivationBlock';
import { EducationalCardsBlock } from '../EducationalCardsBlock/EducationalCardsBlock';
import css from './AutomationsDashboard.module.css';
import { EmailAnalytics } from '../../Analytics/EmailAnalytics/EmailAnalytics';
import { Modal } from 'antd';
import howtoVideo from 'assets/howto-video.png';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useResolutionInfo } from '@mm/hooks';
import { InstallationPageForm } from '../../../../../../dashboard/pages/XRayOnboarding/InstallationPage/InstallationPage';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel, PromoterTypes } from '@mm/state/model/PromoterModel';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { KlaviyoRevenue } from '../../Integrations/NativeIntegrations/components/KlaviyoIntegrations/KlaviyoRevenue';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { SettingsRoutes } from '../../../../../../routes/settings-routes';
import { MOBILE_THRESHOLD } from '../../../../../constants/screenSize';
import { selectRecordsByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { DataQualityTest } from '../../Integrations/NativeIntegrations/components/XRayIngegrations/DataQualityTest/DataQualityTest';

export const useGetEmailPromoters = () => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const [promotersReady, setPromotersReady] = useState(false);
  const [promoterId, setPromoterId] = useState<number | undefined>();

  const storeGetEmailPromoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );

  const { pixelName: whitelabelPixelName = 'X-Ray' } = useWhitelabelConfig();
  const pixelName = `${whitelabelPixelName} Lead Generation Pixel`;

  const promoterCRUDActions = usePromoterCRUDActions();

  const retrieveGetemailPromoters = async () => {
    try {
      if (activeChatbotId) {
        const res = await promoterCRUDActions.retrieveAll(
          activeChatbotId,
          'getemails',
        );
        if (res.length) {
          const firstPromoter = res?.[0];
          setPromoterId(firstPromoter.id);
        } else {
          const promoter = await promoterCRUDActions.create({
            botId: activeChatbotId,
            name: `${pixelName} (1)`,
            type: PromoterTypes.GETEMAILS,
            active: false,
          } as PromoterModel);

          setPromoterId(promoter.id);
        }
      }
    } finally {
      setPromotersReady(true);
    }
  };

  useEffect(() => {
    if (storeGetEmailPromoters.length) {
      setPromoterId(storeGetEmailPromoters[0].id);
      setPromotersReady(true);
    } else {
      retrieveGetemailPromoters();
    }
  }, []);

  return {
    promotersReady,
    promoterId,
  };
};

export const NewDashboard = () => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const emailPromoters = useGetEmailPromoters();

  const session = useSelector(selectSessionSelector);
  const userAttributes = session?.userAttributes;

  const { campaigns: automations } =
    useSelector((state: StoreState) =>
      selectRecordsByUiFilterKind(state, 'automation'),
    ) || {};
  const hasAutomations = !!automations?.length;

  const klaviyoRevenue = useSelector(
    (state: StoreState) => state.klaviyo.revenue,
  );
  const hasRevenue = Object.values(klaviyoRevenue).some(v => v > 0);

  const companyId = useSelector(selectActiveCompanyId);
  const currentUserId = useSelector(selectCurrentUserId);

  const { updateUserAttributes } = useUsersCRUDActions();
  const facebookErrorCRUDActions = useFacebookErrorCRUDActions();
  const iframeMouseOverRef = useRef(false);
  const { screenWidth } = useResolutionInfo();
  const history = useHistory();
  const { showSlideoutMenu } = useViewActions();
  const { isWhitelabelCompany, pixelName = 'X-Ray Pixel' } =
    useWhitelabelConfig();

  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const [showWelcomeSmallModal, setShowWelcomeSmallModal] = useState(false);
  const [showWelcomeModalVideo, setShowWelcomeModalVideo] = useState(false);
  const [startedWatchingVideo, setStartedWatchingVideo] = useState(false);

  const onWindowBlur = () => {
    // Note: this allows us to track if the user clicked the 'play' button in the iframe
    if (iframeMouseOverRef.current) {
      setStartedWatchingVideo(true);
    }
  };

  const onProceedClick = () => {
    showSlideoutMenu('getEmailsEditorNewSettings', {
      promoterId: emailPromoters.promoterId,
    });
  };

  useEffect(() => {
    if (companyId && currentUserId && startedWatchingVideo) {
      // Note: considering seen if the user has not just opened the modal, but also watched the video
      const timeoutHandle = setTimeout(() => {
        updateUserAttributes(companyId, currentUserId, {
          stopShowingWalkthroughVideo: true,
        });
      }, 2.5 * 60 * 1000); // Note: video is ~5 minutes, but users can watch it a faster speed

      return () => {
        clearTimeout(timeoutHandle);
      };
    }
  }, [companyId, currentUserId, startedWatchingVideo]);

  useEffect(() => {
    window.focus();
    window.addEventListener('blur', onWindowBlur);

    return () => {
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  useEffect(() => {
    if (
      !userAttributes.stopShowingWalkthroughVideo &&
      session?.sessionsCount &&
      session.sessionsCount < 4
    ) {
      setShowWelcomeSmallModal(true);
    }
    if (
      currentUserId &&
      companyId &&
      !userAttributes.stopShowingWalkthroughVideo &&
      session?.sessionsCount &&
      session.sessionsCount > 3
    ) {
      // Note: by agreement with BE team, FE decideds when to set the attribute to stop showing the video.
      updateUserAttributes(companyId, currentUserId, {
        stopShowingWalkthroughVideo: true,
      });
    }
  }, [
    userAttributes.stopShowingWalkthroughVideo,
    companyId,
    currentUserId,
    session?.sessionsCount,
  ]);

  const isShopify = useSelector(selectIsShopifyContext);
  const activatedXRay =
    activeFacebookPage?.getemailsActivated ||
    activeFacebookPage?.onceSuccessfullyLoadedGlobalJs;

  useEffect(() => {
    if (activeFacebookPage?.id) {
      facebookErrorCRUDActions.retrieveAll(activeFacebookPage.id);
    }
  }, [activeFacebookPage?.id]);

  return (
    <div className={css.container}>
      <div className={css.errorsContainer}>
        <FacebookPageErrors />
      </div>

      {/* Note: not using our standard modals approach for this modal not to interfere with other modals,
      as we want to persistently show it in the corner of the page till the user watches it or the user is past first 3 sessions*/}
      {!isShopify && (
        <Modal
          className={css.welcomeModalSmall}
          destroyOnClose
          mask={false}
          style={{
            top: `calc(100vh - ${showWelcomeModalVideo ? '540px' : '360px'})`,
          }}
          open={showWelcomeSmallModal && screenWidth > 700}
          title={
            <div className={css.welcomeModalTitle}>
              Welcome to Customers.ai!
            </div>
          }
          cancelText="Close"
          okText="Watch Short Video"
          closeIcon={<CloseOutlined style={{ color: 'white' }} />}
          cancelButtonProps={{
            style: {
              color: 'white',
              display: showWelcomeModalVideo ? 'block' : 'none',
              borderColor: 'white',
              backgroundColor: '#1a4975',
            },
          }}
          okButtonProps={{
            style: {
              display: showWelcomeModalVideo ? 'none' : 'block',
              color: 'white',
              borderColor: 'white',
              backgroundColor: '#1a4975',
            },
            disabled: showWelcomeModalVideo,
            ghost: true,
          }}
          onOk={() => {
            setShowWelcomeModalVideo(true);
            setStartedWatchingVideo(true);
          }}
          onCancel={() => {
            setShowWelcomeSmallModal(false);
          }}
          width={showWelcomeModalVideo ? 690 : 400}
        >
          {showWelcomeModalVideo ? (
            <div
              onMouseOver={() => {
                iframeMouseOverRef.current = true;
              }}
              onMouseOut={() => {
                window.focus();
                iframeMouseOverRef.current = false;
              }}
            >
              <iframe
                width="640"
                height="394"
                src="https://player.vimeo.com/video/856966839?h=4265e990d9&color=3454EF&title=0&byline=0&autoplay=1"
                allowFullScreen
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                title={`${pixelName} Email Capture System in 90 Seconds`}
              />
            </div>
          ) : (
            <img
              src={howtoVideo}
              className={css.welcomeModalSmallImage}
              onClick={() => {
                setShowWelcomeModalVideo(true);
                setStartedWatchingVideo(true);
              }}
            />
          )}
        </Modal>
      )}

      <div className={css.channelsContainer}>
        <div className={css.content}>
          {!activatedXRay && emailPromoters.promotersReady && (
            <InstallationPageForm
              botId={activeChatbotId}
              pageRemoteId={activeFacebookPage?.remoteId}
              canSkip={false}
              promoterId={emailPromoters.promoterId}
              css={{
                container: css.installationForm,
                stepsContainer: css.installationStepsContainer,
                step: css.installationStep,
                thirdStepButton: 'chat-widget-troubleshooting-tool__run-button',
              }}
              canCloseResults={isShopify}
              title={`Install ${pixelName} to Capture your Leads in 2 Steps`}
              subtitle=""
              thirdStepConfig={{
                title: 'Proceed to settings',
                button: 'Proceed',
                onClick: onProceedClick,
              }}
            />
          )}
          {activatedXRay && (
            <EducationalCardsBlock isMobile={isMobile} isShopify={isShopify} />
          )}
        </div>
        {!isShopify && (
          <div className={css.dashboardRightSide}>
            <DataQualityTest compactView />

            {!isMobile && <ActivationBlock />}
            {activeFacebookPage && (
              <div
                className={classNames(css.klaviyo, {
                  [css.hidden]: !hasRevenue,
                })}
              >
                <KlaviyoRevenue
                  dashboardLayout
                  css={{
                    revenueBoxesWrapper: css.klaviyoRevenueBoxes,
                    revenueBox: css.klaviyoRevenueBox,
                    revenueData: css.klaviyoRevenueData,
                  }}
                />
              </div>
            )}
            {!isWhitelabelCompany && (
              <div className={css.refer}>
                <div className={css.referHeading}>
                  <span className={css.referTitle}>
                    Refer & Get Paid Every Month
                  </span>
                  <PrimaryButton
                    className={classNames(css.referButton)}
                    color="transparent"
                    onClick={() =>
                      history.push(SettingsRoutes.affiliates.getLink())
                    }
                  >
                    Learn More
                  </PrimaryButton>
                </div>
                <div>
                  Customer.ai has partnered with PostAffiliatePro to bring you
                  the best affiliate experience possible.
                </div>
              </div>
            )}
            {hasAutomations && (
              <div className={css.analytics}>
                <EmailAnalytics
                  css={{
                    container: classNames(
                      css.topContainer,
                      css.topContainerWithKlaviyoAnalytics,
                    ),
                    header: css.topContainerHeader,
                    goToAutomationsButton: css.goToAutomationsButton,
                    statsContainer: css.statsContainer,
                    box: css.statBox,
                  }}
                  asAutomationRows
                  header="Automations"
                  onlySummaryStats
                  withXRayLeads={false}
                  withNavigateButton
                  withGrowth={false}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
